import { DayOfWeek as ApiDayOfWeek, DraftCafeStation, DraftCafeStationsSchedule, DraftMenu } from "@dining/api-client";
import { Button, Checkbox, Label, makeStyles } from "@fluentui/react-components";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { addMenuItemToLocalMenuItemBank, setSelectedItem, updateDraftSchedule } from "../../reducers/diningReducer";
import { selectedItemSelector, stationSelector, weekMenuSelector } from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";

/* ------------------ TYPES ------------------ */

export type DayOfWeekSelectorInput = {
  id: string;
  daysOfWeek: boolean[];
};

/* ------------------ CONSTANTS ------------------ */

// IMPORTANT: Explicit array — never iterate numeric enums
const WEEK_DAYS: ApiDayOfWeek[] = [
  ApiDayOfWeek.Sunday,
  ApiDayOfWeek.Monday,
  ApiDayOfWeek.Tuesday,
  ApiDayOfWeek.Wednesday,
  ApiDayOfWeek.Thursday,
  ApiDayOfWeek.Friday,
  ApiDayOfWeek.Saturday,
];

/* ------------------ STYLES ------------------ */

const useStyles = makeStyles({
  topRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "15px",
  },
  buttonContainer: {
    marginLeft: "auto",
  },
});

/* ------------------ COMPONENT ------------------ */

export const DayOfWeekSelector: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const selectedItem = useSelector(selectedItemSelector);
  const selectedStation = useSelector(stationSelector);
  const currentMenu = useSelector(weekMenuSelector);

  const stationMenu = currentMenu?.stations?.find((station) => station.id === selectedStation?.id);

  const {
    control,
    handleSubmit,
    reset,
    register,
    clearErrors,
    formState: { isDirty },
  } = useForm<DayOfWeekSelectorInput>({
    mode: "onChange",
    defaultValues: {
      id: "",
      daysOfWeek: new Array(7).fill(false),
    },
  });

  /* ------------------ SYNC FORM WITH SELECTED ITEM ------------------ */

  useEffect(() => {
    if (!selectedItem) {
      reset({
        id: "",
        daysOfWeek: new Array(7).fill(false),
      });
      return;
    }

    const existingWeekDays = stationMenu?.menu?.find((menu) => menu.menuItemId === selectedItem.id)?.weekDays ?? [];

    const checkboxDefaults = WEEK_DAYS.map((day) => existingWeekDays.includes(day));

    reset({
      id: selectedItem.id,
      daysOfWeek: checkboxDefaults,
    });
  }, [selectedItem, stationMenu, reset]);

  /* ------------------ SUBMIT HANDLER ------------------ */

  const onSubmit = (data: DayOfWeekSelectorInput) => {
    const selectedDays: ApiDayOfWeek[] = [];

    data.daysOfWeek.forEach((checked, index) => {
      if (checked) {
        selectedDays.push(WEEK_DAYS[index]);
      }
    });

    updateMenuItemForDraftMenu(data.id, selectedDays);
  };

  /* ------------------ UPDATE MENU LOGIC ------------------ */

  const updateMenuItemForDraftMenu = (id: string, weekDays: ApiDayOfWeek[]) => {
    const menuCopy: DraftCafeStationsSchedule = JSON.parse(JSON.stringify(currentMenu));

    let station = menuCopy?.stations?.find((s: DraftCafeStation) => s.id === selectedStation?.id);

    if (!station) {
      station = DraftCafeStation.fromJS({
        id: selectedStation?.id,
        name: selectedStation?.name,
        menu: [],
      }).toJSON() as DraftCafeStation;

      menuCopy.stations = [...(menuCopy.stations ?? []), station];
    }

    let menuItem = station.menu?.find((m: DraftMenu) => m.menuItemId === id);

    if (menuItem) {
      menuItem.weekDays = weekDays;
      menuItem.isPublished = false;
    } else {
      selectedItem && dispatch(addMenuItemToLocalMenuItemBank(selectedItem));

      if (!station.menu) {
        station.menu = [];
      }

      station.menu.push(
        DraftMenu.fromJS({
          weekDays,
          isPublished: false,
          publishedMenuId: null,
          menuItemId: id,
        }).toJSON(),
      );
    }

    dispatch(updateDraftSchedule(menuCopy));
    dispatch(setSelectedItem());
  };

  /* ------------------ RENDER ------------------ */

  return (
    <div className="menuContainer">
      <Label>Occurs on:</Label>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.topRow}>
          {/* Hidden ID field */}
          <input hidden readOnly {...register("id")} value={selectedItem?.id ?? ""} aria-hidden />

          {/* Weekday Checkboxes (Mon–Fri visible) */}
          {WEEK_DAYS.map((day, index) => {
            const label = ApiDayOfWeek[day];
            const fieldName = `daysOfWeek.${index}` as const;

            if (index > 0 && index < 6) {
              return (
                <Controller
                  key={day}
                  control={control}
                  name={fieldName}
                  render={({ field }) => (
                    <Checkbox
                      label={label}
                      aria-label={`Occurs on: ${label}`}
                      checked={field.value === true}
                      disabled={!selectedItem}
                      data-testid={`ItemScheduleCheckboxForDay${label}`}
                      onChange={(_, data) => {
                        if (data.checked) clearErrors("daysOfWeek");
                        field.onChange(data.checked);
                      }}
                    />
                  )}
                />
              );
            }

            // Weekend hidden
            return <input key={day} type="checkbox" hidden readOnly {...register(fieldName)} />;
          })}

          {/* Submit Button */}
          <div className={classes.buttonContainer}>
            <Button appearance="primary" type="submit" data-testid="DoWSubmitBtn" disabled={!selectedItem || !isDirty}>
              {stationMenu?.menu?.some((menu) => menu.menuItemId === selectedItem?.id) ? "Update" : "Add to menu"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
